import { IContext } from "overmind"
import { AppModules, AppStateConfig } from "src/state"
import { AppState } from "src/state/app/_types"

export * from "src/state/app/_types";

export type Context = IContext<typeof AppStateConfig>

export type RootAppState = Context['state']

export type ModuleKeys = keyof typeof AppModules

export interface Action<Input extends any = void, Output extends any = void> {
    (context: {
        state: Context['state'];
        actions: Context['actions'];
        effects: Context['effects'];
        reaction: any;
        addMutationListener: any;
        addFlushListener: any;
    }, payload: Input): Output;
}

export interface AsyncAction<Input extends any = void, Output extends any = void> {
    (context: {
        state: Context['state'];
        actions: Context['actions'];
        effects: Context['effects'];
        reaction: any;
        addMutationListener: any;
        addFlushListener: any;
    }, payload: Input): Promise<Output>;
}

export type GenericDictionary = {
    [index: string]: any
}

export type GenericError = {
    code: string;
    text: string;
}

export type GraphQLError = {
    extensions: {
        path: string;
        code: string;
    },
    message: string;
}

export type GraphQLSubscriptionResultsPayload<T> = {
    error: GraphQLError | GraphQLError[] | null,
    data: T | null
}

export type GraphQLErrorResponse = {
    response: {
        errors: GraphQLError[],
        status: number;
    }
}

export type Subscriptions = {
    [index: string]: {
        unsubscribe: () => void;
    }
}

export type SubscriptionInitParams = {
    auth: AppState['auth']
}

export function typedEntries<T extends object>(obj: T) {
    return Object.entries(obj) as {
        [K in keyof T]-?: [K, T[K]];
    }[keyof T][];
}

export type ViewValueType =
    | "text" 
    | "number" 
    | "boolean" 
    | "date" 
    | "datetime" 
    | "time"
    | "enum" 
    | "json" 
    | "currency" 
    | "email" 
    | "phone" 
    | "url" 
    | "badge" 
    | "rrule" 
    | "multi-select" 
    | "calendar-link"
    | "area-status-badge"
    | "area-type-badge"
    | "booking-status-badge"
    | "booking-type-badge"
    | "document-status-badge"
    | "document-type-badge"
    | "document-role-badge"
    | "guideline-status-badge"
    | "guideline-type-badge"
    | "invoice-target-badge"
    | "message-type-badge"
    | "rate-type-badge"
    | "tenant-status-badge"
    | "tenant-type-badge"
    | "thread-status-badge"
    | "thread-type-badge"
    | "user-status-badge"
    | "user-type-badge"
    | "user-role-badge"
    


export type ViewSchemaFieldItem<E, K extends keyof E = keyof E> = {
    label: string;
    field: K;
    type: ViewValueType;
    relation?: string;  
    relation_field?: keyof NonNullable<E[RelKeys<E>]>;
};

type RelKeys<T> = {
    [K in keyof T]-?: NonNullable<T[K]> extends object ? K : never
}[keyof T];

type ScalarKeys<T> = {
    [K in keyof T]-?: T[K] extends string | number | boolean ? K : never
}[keyof T];

// Case A: enum stored directly on the parent (e.g., currentItem.status)
export type ViewEnumScalarItem<T> = {
    label: string;
    field: ScalarKeys<T>;   // keyof T that is scalar
    type: ViewValueType; 
    relation_field?: keyof NonNullable<T[ScalarKeys<T>]>; // child key on that related type, e.g. "display"
};

// Case B: enum label comes from a related object (e.g., currentItem.booking_status.display)
export type ViewEnumRelationItem<T> = {
    label: string;
    field: RelKeys<T>;                          // relation key, e.g. "booking_status"
    relation_field: keyof NonNullable<T[RelKeys<T>]>; // child key on that related type, e.g. "display"
    type: ViewValueType;
};

export type ViewEnumItem<T> = ViewEnumScalarItem<T> | ViewEnumRelationItem<T>;


export type ViewSchemaType<T> = {
    enums: ViewEnumItem<T>[];
    fields: ViewSchemaFieldItem<T>[];
    relations: {
        [K in RelKeys<T>]?: {
            relation: K;
            label: string;
            fields: ViewSchemaFieldItem<NonNullable<T[K]>>[];
        }
    };
};

export type GuidelineMetaDataQuestion = {
    question: string;
    answer: string | undefined | null;
    type: 'text' | 'textarea' |'number' | 'boolean' | 'date' | 'datetime' | 'time' | 'enum' | 'currency' | 'email' | 'phone' | 'url' | 'multi-select' | 'rating';
    required: boolean;
    options?: (string | { label: string; value: string })[];
}

export type GuidelineMetaData = {
    questions: GuidelineMetaDataQuestion[];
}